'use client'

import { useState, useEffect, useRef, useCallback } from 'react';
import { useSession } from 'next-auth/react';
import { useRouter } from 'next/navigation';
import { MdOutlineSend, MdAttachFile, MdEmojiEmotions, MdMic, MdArrowBack, MdSearch, MdMoreVert } from "react-icons/md";
import { BsCheck, BsCheckAll } from "react-icons/bs";
import { FaCircle } from "react-icons/fa";

let io = null;
if (typeof window !== 'undefined') {
    io = require('socket.io-client').default || require('socket.io-client');
}

const EMOJI_PICKER = ['😀', '😂', '😍', '🤔', '👍', '🎉', '🔥', '💯', '✨', '❤️', '😢', '👏'];

export default function ChatPage() {
    const router = useRouter();
    const { data: session, status } = useSession();
    const [socket, setSocket] = useState(null);
    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [messages, setMessages] = useState([]);
    const [messageInput, setMessageInput] = useState('');
    const [loading, setLoading] = useState(true);
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [typingUsers, setTypingUsers] = useState(new Set());
    const [unreadCount, setUnreadCount] = useState({});
    const [isMobile, setIsMobile] = useState(false);
    const messagesEndRef = useRef(null);
    const typingTimeoutRef = useRef(null);
    const socketRef = useRef(null);
    const emojiPickerRef = useRef(null);

    // Detect mobile
    useEffect(() => {
        const checkMobile = () => setIsMobile(window.innerWidth < 768);
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Handle click outside emoji picker
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target)) {
                setShowEmojiPicker(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (status === 'loading') return;
        if (!session?.user?.id) {
            router.push('/login');
            return;
        }

        if (typeof window === 'undefined' || !io) return;

        const newSocket = io(undefined, {
            reconnection: true,
            reconnectionDelay: 1000,
            reconnectionDelayMax: 5000,
            reconnectionAttempts: 5,
            transports: ['websocket', 'polling']
        });

        socketRef.current = newSocket;

        newSocket.on('connect', () => {
            console.log('Connected to chat server');
            newSocket.emit('user_join', {
                userId: session.user.id,
                userName: session.user.name || 'Anonymous',
                userEmail: session.user.email || '',
                profilePhoto: session.user.image || ''
            });
            newSocket.emit('get_active_users');
        });

        newSocket.on('active_users_list', (activeUsers) => {
            const otherUsers = activeUsers.filter(u => u.userId !== session.user.id);
            setUsers(otherUsers);
            setLoading(false);
        });

        newSocket.on('user_status_changed', (data) => {
            setUsers(prevUsers => {
                const userIndex = prevUsers.findIndex(u => u.userId === data.userId);
                if (userIndex > -1) {
                    const updated = [...prevUsers];
                    updated[userIndex] = { ...updated[userIndex], status: data.status };
                    return updated;
                }
                if (data.status === 'online') {
                    return [...prevUsers, { userId: data.userId, name: data.name, status: 'online', profilePhoto: data.profilePhoto }];
                }
                return prevUsers;
            });
        });

        newSocket.on('receive_message', (data) => {
            setMessages(prev => [...prev, data]);
            if (selectedUser?.userId !== data.senderId) {
                setUnreadCount(prev => ({
                    ...prev,
                    [data.senderId]: (prev[data.senderId] || 0) + 1
                }));
            }
        });

        newSocket.on('user_typing', (data) => {
            setTypingUsers(prev => new Set([...prev, data.senderId]));
        });

        newSocket.on('user_stopped_typing', (data) => {
            setTypingUsers(prev => {
                const updated = new Set(prev);
                updated.delete(data.senderId);
                return updated;
            });
        });

        setSocket(newSocket);

        return () => {
            newSocket.disconnect();
        };
    }, [session, status, router]);

    const scrollToBottom = useCallback(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, []);

    useEffect(() => {
        scrollToBottom();
    }, [messages, scrollToBottom]);

    const loadMessages = useCallback(async (user) => {
        try {
            const response = await fetch(`/api/messages?recipientId=${user.userId}&limit=100`);
            const data = await response.json();
            if (data.success) {
                setMessages(data.messages);
                setUnreadCount(prev => ({ ...prev, [user.userId]: 0 }));
            }
        } catch (error) {
            console.error('Error loading messages:', error);
        }
    }, [session]);

    const handleSelectUser = useCallback((user) => {
        setSelectedUser(user);
        if (isMobile) {
            document.body.style.overflow = 'hidden';
        }
        loadMessages(user);
        setShowEmojiPicker(false);
    }, [loadMessages, isMobile]);

    const sendMessage = useCallback(() => {
        if (!messageInput.trim() || !selectedUser || !socketRef.current) return;

        const messageData = {
            recipientId: selectedUser.userId,
            message: messageInput.trim(),
            timestamp: new Date().toISOString()
        };

        socketRef.current.emit('send_message', messageData);

        setMessages(prev => [...prev, {
            senderId: session.user.id,
            senderName: session.user.name,
            recipientId: selectedUser.userId,
            message: messageInput.trim(),
            timestamp: new Date().toISOString(),
            read: false,
            status: 'sent'
        }]);

        setMessageInput('');
        setShowEmojiPicker(false);
    }, [messageInput, selectedUser, session]);

    const handleTyping = useCallback(() => {
        if (!selectedUser || !socketRef.current) return;

        socketRef.current.emit('user_typing', { recipientId: selectedUser.userId });

        if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);

        typingTimeoutRef.current = setTimeout(() => {
            socketRef.current.emit('user_stopped_typing', { recipientId: selectedUser.userId });
        }, 1000);
    }, [selectedUser]);

    const handleBack = () => {
        setSelectedUser(null);
        if (isMobile) {
            document.body.style.overflow = 'auto';
        }
    };

    if (status === 'loading') {
        return (
            <div className="loading-container">
                <div className="loading-spinner"></div>
            </div>
        );
    }

    if (!session?.user) {
        router.push('/login');
        return null;
    }

    const filteredUsers = users.filter(user =>
        user.name?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <div className="chat-container">
            <style jsx global>{`
                * {
                    margin: 0;
                    padding: 0;
                    box-sizing: border-box;
                }

                body {
                    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
                        'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
                    -webkit-font-smoothing: antialiased;
                    -moz-osx-font-smoothing: grayscale;
                    background: #f0f2f5;
                }

                .chat-container {
                    height: 100vh;
                    width: 100vw;
                    max-width: 100%;
                    background: #fff;
                    display: flex;
                    position: relative;
                    overflow: hidden;
                }

                /* Loading */
                .loading-container {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    height: 100vh;
                    width: 100vw;
                }

                .loading-spinner {
                    width: 50px;
                    height: 50px;
                    border: 3px solid #f3f3f3;
                    border-top: 3px solid #25d366;
                    border-radius: 50%;
                    animation: spin 1s linear infinite;
                }

                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }

                /* Sidebar */
                .sidebar {
                    width: 100%;
                    max-width: 420px;
                    background: #fff;
                    display: flex;
                    flex-direction: column;
                    height: 100%;
                    border-right: 1px solid #e9edef;
                    transition: transform 0.3s ease;
                    position: absolute;
                    top: 0;
                    left: 0;
                    z-index: 100;
                }

                .sidebar-header {
                    background: #008069;
                    padding: 0.75rem 1rem;
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                    color: white;
                }

                .user-avatar {
                    width: 40px;
                    height: 40px;
                    border-radius: 50%;
                    background: #ddd;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 1.2rem;
                    font-weight: 600;
                    color: #666;
                    overflow: hidden;
                }

                .user-avatar img {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                }

                .user-info h2 {
                    font-size: 1.1rem;
                    font-weight: 600;
                }

                .user-info p {
                    font-size: 0.8rem;
                    opacity: 0.9;
                }

                .header-actions {
                    margin-left: auto;
                    display: flex;
                    gap: 1.5rem;
                    align-items: center;
                }

                .header-actions button {
                    background: none;
                    border: none;
                    color: white;
                    font-size: 1.3rem;
                    cursor: pointer;
                    padding: 0.25rem;
                }

                /* Search */
                .search-container {
                    padding: 0.75rem 1rem;
                    background: #fff;
                }

                .search-box {
                    background: #f0f2f5;
                    border-radius: 8px;
                    padding: 0.6rem 1rem;
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    color: #667781;
                }

                .search-box input {
                    border: none;
                    background: none;
                    outline: none;
                    flex: 1;
                    font-size: 0.95rem;
                    color: #111b21;
                }

                /* Users List */
                .users-list {
                    flex: 1;
                    overflow-y: auto;
                    background: #fff;
                }

                .user-item {
                    display: flex;
                    align-items: center;
                    padding: 0.75rem 1rem;
                    gap: 1rem;
                    border-bottom: 1px solid #f0f2f5;
                    cursor: pointer;
                    transition: background 0.2s;
                }

                .user-item:hover {
                    background: #f5f6f6;
                }

                .user-item.active {
                    background: #f0f2f5;
                }

                .user-avatar-small {
                    width: 49px;
                    height: 49px;
                    border-radius: 50%;
                    background: #ddd;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 1rem;
                    font-weight: 600;
                    color: #666;
                    position: relative;
                    overflow: hidden;
                }

                .online-status {
                    position: absolute;
                    bottom: 2px;
                    right: 2px;
                    width: 12px;
                    height: 12px;
                    background: #31a24c;
                    border: 2px solid white;
                    border-radius: 50%;
                }

                .user-details {
                    flex: 1;
                    min-width: 0;
                }

                .user-details h3 {
                    font-size: 1rem;
                    font-weight: 500;
                    color: #111b21;
                    margin-bottom: 0.2rem;
                    display: flex;
                    justify-content: space-between;
                }

                .last-message {
                    font-size: 0.85rem;
                    color: #667781;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }

                .message-time {
                    font-size: 0.75rem;
                    color: #667781;
                }

                .unread-badge {
                    background: #25d366;
                    color: white;
                    font-size: 0.75rem;
                    padding: 0.1rem 0.4rem;
                    border-radius: 10px;
                    min-width: 20px;
                    text-align: center;
                }

                /* Chat Area */
                .chat-area {
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    height: 100%;
                    background: #efeae2;
                    position: absolute;
                    top: 0;
                    right: 0;
                    width: 100%;
                    z-index: 101;
                    transition: transform 0.3s ease;
                }

                .chat-area.hidden {
                    transform: translateX(100%);
                }

                .chat-area.visible {
                    transform: translateX(0);
                }

                .chat-header {
                    background: #008069;
                    padding: 0.75rem 1rem;
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                    color: white;
                }

                .back-button {
                    background: none;
                    border: none;
                    color: white;
                    font-size: 1.3rem;
                    cursor: pointer;
                    padding: 0.25rem;
                    display: flex;
                    align-items: center;
                }

                .messages-container {
                    flex: 1;
                    overflow-y: auto;
                    padding: 1rem;
                    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 100 100'%3E%3Cg fill-rule='evenodd'%3E%3Cg fill='%23008069' fill-opacity='0.05'%3E%3Ccircle cx='25' cy='25' r='2'/%3E%3Ccircle cx='75' cy='75' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
                }

                .message-wrapper {
                    display: flex;
                    margin-bottom: 0.5rem;
                }

                .message-wrapper.sent {
                    justify-content: flex-end;
                }

                .message-wrapper.received {
                    justify-content: flex-start;
                }

                .message-bubble {
                    max-width: 65%;
                    padding: 0.5rem 0.75rem;
                    border-radius: 7.5px;
                    position: relative;
                    word-wrap: break-word;
                }

                .message-bubble.sent {
                    background: #d9fdd3;
                    border-top-right-radius: 0;
                }

                .message-bubble.received {
                    background: white;
                    border-top-left-radius: 0;
                    box-shadow: 0 1px 0.5px rgba(0,0,0,0.08);
                }

                .message-text {
                    font-size: 0.95rem;
                    line-height: 1.4;
                    color: #111b21;
                }

                .message-time {
                    font-size: 0.7rem;
                    color: #667781;
                    display: flex;
                    align-items: center;
                    justify-content: flex-end;
                    gap: 0.2rem;
                    margin-top: 0.2rem;
                }

                .typing-indicator {
                    display: inline-flex;
                    align-items: center;
                    gap: 0.3rem;
                    padding: 0.5rem 0.75rem;
                    background: white;
                    border-radius: 7.5px;
                    border-top-left-radius: 0;
                    box-shadow: 0 1px 0.5px rgba(0,0,0,0.08);
                }

                .typing-dot {
                    width: 6px;
                    height: 6px;
                    background: #667781;
                    border-radius: 50%;
                    animation: typing 1.4s infinite;
                }

                .typing-dot:nth-child(2) { animation-delay: 0.2s; }
                .typing-dot:nth-child(3) { animation-delay: 0.4s; }

                @keyframes typing {
                    0%, 60%, 100% { transform: translateY(0); }
                    30% { transform: translateY(-5px); }
                }

                /* Input Area */
                .input-area {
                    background: #f0f2f5;
                    padding: 0.6rem 1rem;
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    border-top: 1px solid #e9edef;
                }

                .emoji-picker {
                    position: absolute;
                    bottom: 100%;
                    left: 0;
                    background: white;
                    border-radius: 8px;
                    padding: 0.75rem;
                    display: grid;
                    grid-template-columns: repeat(6, 1fr);
                    gap: 0.5rem;
                    box-shadow: 0 2px 20px rgba(0,0,0,0.15);
                    margin-bottom: 0.5rem;
                    z-index: 1000;
                }

                .emoji-button {
                    background: none;
                    border: none;
                    font-size: 1.5rem;
                    cursor: pointer;
                    padding: 0.3rem;
                    border-radius: 4px;
                    transition: background 0.2s;
                }

                .emoji-button:hover {
                    background: #f0f2f5;
                }

                .message-input {
                    flex: 1;
                    background: white;
                    border: none;
                    border-radius: 8px;
                    padding: 0.6rem 1rem;
                    font-size: 0.95rem;
                    outline: none;
                    color: #111b21;
                    min-height: 40px;
                    max-height: 120px;
                    resize: none;
                    font-family: inherit;
                }

                .message-input::placeholder {
                    color: #667781;
                }

                .send-button {
                    background: #25d366;
                    border: none;
                    border-radius: 50%;
                    width: 40px;
                    height: 40px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: white;
                    cursor: pointer;
                    transition: background 0.2s;
                }

                .send-button:disabled {
                    background: #a6a6a6;
                    cursor: not-allowed;
                }

                .send-button:not(:disabled):hover {
                    background: #1da851;
                }

                .action-button {
                    background: none;
                    border: none;
                    color: #54656f;
                    font-size: 1.3rem;
                    cursor: pointer;
                    padding: 0.5rem;
                }

                /* Media Queries */
                @media (min-width: 768px) {
                    .chat-container {
                        flex-direction: row;
                    }
                    
                    .sidebar {
                        position: relative;
                        width: 30%;
                        max-width: 400px;
                    }
                    
                    .chat-area {
                        position: relative;
                        width: 70%;
                    }
                    
                    .back-button {
                        display: none;
                    }
                    
                    .sidebar.hidden-mobile {
                        display: flex;
                    }
                    
                    .chat-area.hidden {
                        transform: none;
                    }
                }

                @media (max-width: 767px) {
                    .sidebar.hidden-mobile {
                        display: none;
                    }
                    
                    .chat-area.visible-mobile {
                        display: flex;
                    }
                         .sidebar {
                    width: 100%;
                    max-width: 100%;
                }
                }

                /* Scrollbar */
                ::-webkit-scrollbar {
                    width: 6px;
                }

                ::-webkit-scrollbar-track {
                    background: transparent;
                }

                ::-webkit-scrollbar-thumb {
                    background: #b0b0b0;
                    border-radius: 3px;
                }

                ::-webkit-scrollbar-thumb:hover {
                    background: #888;
                }
            `}</style>

            {/* Sidebar */}
            <div className={`sidebar ${selectedUser && isMobile ? 'hidden-mobile' : ''}`}>
                <div className="sidebar-header">
                    <div className="user-avatar">
                        {session.user.image ? (
                            <img src={session.user.image} alt={session.user.name} />
                        ) : (
                            session.user.name?.charAt(0)?.toUpperCase() || 'U'
                        )}
                    </div>
                    <div className="user-info">
                        <h2>{session.user.name || 'User'}</h2>
                        <p>Available</p>
                    </div>
                    {/* <div className="header-actions">
                        <button>
                            <MdSearch />
                        </button>
                        <button>
                            <MdMoreVert />
                        </button>
                    </div> */}
                </div>

                <div className="search-container">
                    <div className="search-box">
                        <MdSearch />
                        <input
                            type="text"
                            placeholder="Search or start new chat"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                </div>
 
                <div className="users-list" data-lenis-prevent="true">
                    {loading ? (
                        <div style={{ padding: '2rem', textAlign: 'center', color: '#667781' }}>
                            Loading...
                        </div>
                    ) : filteredUsers.length === 0 ? (
                        <div style={{ padding: '2rem', textAlign: 'center', color: '#667781' }}>
                            {searchQuery ? 'No users found' : 'No users online'}
                        </div>
                    ) : (
                        filteredUsers.map(user => (
                            <div
                                key={user.userId}
                                className={`user-item ${selectedUser?.userId === user.userId ? 'active' : ''}`}
                                onClick={() => handleSelectUser(user)}
                            >
                                <div className="user-avatar-small">
                                    {user.profilePhoto ? (
                                        <img src={user.profilePhoto} alt={user.name} />
                                    ) : (
                                        user.name?.charAt(0)?.toUpperCase() || 'U'
                                    )}
                                    {user.status === 'online' && <div className="online-status" />}
                                </div>
                                <div className="user-details">
                                    <h3>
                                        {user.name || 'Unknown'}
                                        <span className="message-time">
                                            {user.lastSeen}
                                        </span>
                                    </h3>
                                    <div className="last-message">
                                        {typingUsers.has(user.userId) ? (
                                            <div className="typing-indicator">
                                                <div className="typing-dot" />
                                                <div className="typing-dot" />
                                                <div className="typing-dot" />
                                            </div>
                                        ) : (
                                            <span>{user.lastMessage || 'Start chatting'}</span>
                                        )}
                                    </div>
                                </div>
                                {unreadCount[user.userId] > 0 && (
                                    <div className="unread-badge">
                                        {unreadCount[user.userId]}
                                    </div>
                                )}
                            </div>
                        ))
                    )}
                </div>
            </div>

            {/* Chat Area */}
            <div className={`chat-area ${!selectedUser && isMobile ? 'hidden' : 'visible'} ${selectedUser ? 'visible-mobile' : ''}`}>
                {selectedUser ? (
                    <>
                        <div className="chat-header">
                            <button className="back-button" onClick={handleBack}>
                                <MdArrowBack />
                            </button>
                            <div className="user-avatar-small">
                                {selectedUser.profilePhoto ? (
                                    <img src={selectedUser.profilePhoto} alt={selectedUser.name} />
                                ) : (
                                    selectedUser.name?.charAt(0)?.toUpperCase() || 'U'
                                )}
                                {selectedUser.status === 'online' && <div className="online-status" />}
                            </div>
                            <div className="user-info">
                                <h2>{selectedUser.name || 'Unknown'}</h2>
                                <p>
                                    {typingUsers.has(selectedUser.userId) 
                                        ? 'typing...' 
                                        : selectedUser.status === 'online' 
                                            ? 'online' 
                                            : 'offline'}
                                </p>
                            </div>
                            {/* <div className="header-actions">
                                <button>
                                    <MdSearch />
                                </button>
                                <button>
                                    <MdMoreVert />
                                </button>
                            </div> */}
                        </div>

                        <div className="messages-container" ref={messagesEndRef}>
                            {messages.length === 0 ? (
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    height: '100%',
                                    color: '#667781',
                                    textAlign: 'center',
                                    padding: '2rem'
                                }}>
                                    <div>
                                        <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>💬</div>
                                        <p style={{ fontSize: '0.9rem' }}>Start a conversation with {selectedUser.name}</p>
                                        <p style={{ fontSize: '0.8rem', marginTop: '0.5rem' }}>Messages are end-to-end encrypted</p>
                                    </div>
                                </div>
                            ) : (
                                messages.map((msg, idx) => {
                                    const isOwn = msg.senderId === session.user.id;
                                    const time = new Date(msg.timestamp).toLocaleTimeString([], { 
                                        hour: '2-digit', 
                                        minute: '2-digit' 
                                    });
                                    
                                    return (
                                        <div key={idx} className={`message-wrapper ${isOwn ? 'sent' : 'received'}`}>
                                            <div className={`message-bubble ${isOwn ? 'sent' : 'received'}`}>
                                                <div className="message-text">{msg.message}</div>
                                                <div className="message-time">
                                                    {time}
                                                    {isOwn && (
                                                        msg.read ? (
                                                            <BsCheckAll style={{ color: '#53bdeb' }} />
                                                        ) : (
                                                            <BsCheck style={{ color: '#667781' }} />
                                                        )
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                            {typingUsers.has(selectedUser.userId) && (
                                <div className="message-wrapper received">
                                    <div className="typing-indicator">
                                        <div className="typing-dot" />
                                        <div className="typing-dot" />
                                        <div className="typing-dot" />
                                    </div>
                                </div>
                            )}
                            <div ref={messagesEndRef} />
                        </div>

                        <div className="input-area">
                            <button className="action-button">
                                <MdAttachFile />
                            </button>
                            
                            <div style={{ position: 'relative' }} ref={emojiPickerRef}>
                                <button 
                                    className="action-button"
                                    onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                                >
                                    <MdEmojiEmotions />
                                </button>
                                
                                {showEmojiPicker && (
                                    <div className="emoji-picker">
                                        {EMOJI_PICKER.map((emoji, idx) => (
                                            <button
                                                key={idx}
                                                className="emoji-button"
                                                onClick={() => {
                                                    setMessageInput(prev => prev + emoji);
                                                    setShowEmojiPicker(false);
                                                }}
                                            >
                                                {emoji}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <textarea
                                className="message-input"
                                value={messageInput}
                                onChange={(e) => setMessageInput(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && !e.shiftKey) {
                                        e.preventDefault();
                                        sendMessage();
                                    }
                                }}
                                onInput={handleTyping}
                                placeholder="Type a message"
                                rows="1"
                            />

                            {messageInput.trim() ? (
                                <button 
                                    className="send-button"
                                    onClick={sendMessage}
                                >
                                    <MdOutlineSend />
                                </button>
                            ) : (
                                <button className="action-button">
                                    <MdMic />
                                </button>
                            )}
                        </div>
                    </>
                ) : !isMobile && (
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%',
                        background: '#f0f2f5',
                        textAlign: 'center',
                        padding: '2rem'
                    }}>
                        <div>
                            <div style={{ 
                                width: '250px', 
                                height: '250px', 
                                margin: '0 auto 2rem',
                                backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'250\' height=\'250\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M187.5 125c0 34.5-28 62.5-62.5 62.5S62.5 159.5 62.5 125 90.5 62.5 125 62.5s62.5 28 62.5 62.5z\' fill=\'%23d1f4cc\'/%3E%3Cpath d=\'M125 175c27.6 0 50-22.4 50-50s-22.4-50-50-50-50 22.4-50 50 22.4 50 50 50zm0 10c-33.1 0-60-26.9-60-60s26.9-60 60-60 60 26.9 60 60-26.9 60-60 60z\' fill=\'%2325d366\'/%3E%3Cpath d=\'M140 110l-15 15-15-15m-10 30h50\' stroke=\'%2325d366\' stroke-width=\'3\' fill=\'none\'/%3E%3C/svg%3E")',
                                backgroundRepeat: 'no-repeat',
                                backgroundPosition: 'center'
                            }} />
                            <h2 style={{ fontSize: '2rem', color: '#41525d', marginBottom: '1rem' }}>
                                Chat Now
                            </h2>
                            <p style={{ color: '#667781', lineHeight: '1.5' }}>
                               Lorem, ipsum.
                            </p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}